import { useTranslation } from 'react-i18next';
import { CalendarRange } from 'lucide-react';
import { P } from '@massar/shared/permissions';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { InfoTooltip } from '@/components/ui/tooltip';
import { useCurrentPeriod } from '@/features/accounting/periods.queries';
import { usePermission } from '@/hooks/usePermission';
import { statusVariant } from '@/components/feedback/StatusBadge';

/**
 * The current accounting period, always visible in the chrome (docs/04 §1).
 *
 * It is here because "why was my entry rejected?" is almost always "the period is
 * locked" (I6), and the answer should be on screen before the question is asked.
 */
export const PeriodBadge = () => {
  const { t } = useTranslation(['common', 'accounting']);
  const can = usePermission();
  const allowed = can(P.PERIOD_VIEW, null);

  const { period, isLoading } = useCurrentPeriod({ enabled: allowed });

  if (!allowed) return null;
  if (isLoading) return <Skeleton className="h-6 w-32" />;

  if (!period) {
    return (
      <InfoTooltip content={t('common:period.noneHint')}>
        <Badge variant="destructive" className="gap-1.5">
          <CalendarRange className="size-3.5" />
          {t('common:period.none')}
        </Badge>
      </InfoTooltip>
    );
  }

  return (
    <Badge variant={statusVariant(period.status)} className="gap-1.5">
      <CalendarRange className="size-3.5" />
      <span className="font-medium">{period.code}</span>
      <span className="opacity-80">·</span>
      <span>{t(`common:status.${period.status}`, { defaultValue: period.status })}</span>
    </Badge>
  );
};

export default PeriodBadge;
